// TODO: Volver aquí para arreglar el botón de pánico

// src/exercises/SpeakingExercise.jsx
//
// ÚNICO CAMBIO vs versión original:
//   ✅ Import de ExerciseTokensGuard (1 línea)
//   ✅ El JSX final envuelto en <ExerciseTokensGuard> (2 líneas)
//   ✅ TODO LO DEMÁS intacto — no se tocó ninguna otra línea
//   🔥 NUEVO: Envuelto en <ScanBorderWrapper> y burbuja reemplazada por <SpacyThinkingBubble>
//   🔧 PARCHES: Web Speech API en lugar de OpenAI TTS/Whisper (modo desarrollo)
//   🆕 NUEVO: Botones de ayuda reemplazados por SpacyPanicButtons

import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { LIMITES, avisaDelTope, motivoDelFallo } from '../limites';
import { useNavigate } from 'react-router-dom';
import { Mic, Send, Bot, User, Volume2, Clock, RefreshCw, Eye, MessageCircle, AlertCircle, History, Maximize, Minimize, ZoomIn, ZoomOut, Loader, ArrowRight, ArrowLeft, CheckCircle, Sparkles, BookOpen, Zap, Drama, Globe, Briefcase, GraduationCap, Flame, Award } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './Exercise.css';
import './SpeakingExercise.css';
import { launchConfetti, showAchievement } from '../utils/effects.js';
// El cierre de la clase dejó de ser un cartel encima de la pantalla y pasó a
// ser un mensaje más de Spacy dentro del chat. Ver CierreDeLeccion.jsx.
import CierreDeLeccion from './CierreDeLeccion';
import { apuntaReto, retosDe, siguienteReto } from './retosSuperados';
import useLiveTranscription from './useLiveTranscription.js';
import VoiceCoachStrip from './VoiceCoachStrip.jsx';

// ── NUEVO: Guard de tokens para este ejercicio ───────────────────────────────
import ExerciseTokensGuard from '../components/guards/ExerciseTokensGuard';
import { useSubscriptionStatus, PaywallOverlay } from '../components/guards/SubscriptionGuard';

// 🆕 NUEVO: Botones de pánico para el modo tutor
import SpacyPanicButtons from './SpacyPanicButtons';
import './spacy-panic.css';

// ── Video en vez de gif ─────────────────────────────────────────────────────
// Los gifs de Spacy son de 1080x1080 y pesan 44 MB entre todos; el de hablar,
// el solo, 26,5 MB. Los mismos videos a 512 px pesan 664 KB entre todos y a
// tamano de pantalla se ven igual. Un gif no comprime entre fotogramas —guarda
// cada cuadro casi entero—, un video si. Los .gif siguen en assets/ por si
// hiciera falta volver.
import spacyNormal from '../assets/spacy-normal.mp4';
import spacyListening from '../assets/spacy-escuchando.mp4';
import spacySpeaking from '../assets/spacy-hablando.mp4';
import spacyThinking from '../assets/spacy-pensando.mp4';
import spacyError from '../assets/spacy-error.mp4';

// Las rutas ahora usan "./" porque los archivos son "vecinos" en la misma carpeta
import { SpacyThinkingBubble, ScanBorderWrapper } from "../components/SpacyScanner";
import "../components/spacy-scanner.css";

// 🎨 NUEVO RENDERER COMPARTIDO
import SpacyMessageRenderer from './SpacyMessageRenderer';
import './spacy-renderer.css';

// --- Funciones de Utilidad (Sin cambios) ---
const correctCommonErrors = (text) => {
    const corrections = {
        'bola': 'hola', 'ola': 'hola', 'komo': 'cómo', 'komo estas': 'cómo estás',
        'grasias': 'gracias', 'porfavor': 'por favor', 'porke': 'porque', 'ke': 'qué',
        'kiero': 'quiero', 'anser': 'answer', 'inglis': 'inglés',
        'helo': 'hello', 'jello': 'hello', 'wat': 'what', 'wats': 'what\'s',
        'wheres': 'where\'s', 'hows': 'how\'s', 'tanks': 'thanks', 'plis': 'please',
        'plz': 'please', 'sori': 'sorry', 'becos': 'because', 'cud': 'could',
        'wud': 'would', 'shud': 'should'
    };
    let correctedText = text.toLowerCase();
    Object.keys(corrections).forEach(error => {
        const regex = new RegExp(`\\b${error}\\b`, 'gi');
        correctedText = correctedText.replace(regex, corrections[error]);
    });
    return correctedText;
};

const normalizePunctuation = (text) => {
    return text
        .replace(/\s*\.\s*/g, '. ')
        .replace(/\s*,\s*/g, ', ')
        .replace(/\s*\?\s*/g, '? ')
        .replace(/\s*!\s*/g, '! ')
        .replace(/\s+\./g, '.')
        .replace(/\s+,/g, ',')
        .trim();
};

const improveCapitalization = (text) => {
    if (!text) return text;
    let result = text.charAt(0).toUpperCase() + text.slice(1);
    result = result.replace(/([.!?])\s+(\w)/g, (match, punct, letter) => {
        return `${punct} ${letter.toUpperCase()}`;
    });
    return result;
};

const autoPunctuate = (text) => {
    if (!text.trim()) return text;
    let correctedText = text.trim();
    const questionWords = ['what', 'how', 'why', 'when', 'where', 'who', 'which', 'can you', 'could you', 'would you', 'do you', 'does', 'is', 'are', 'will', 'should'];
    const isQuestion = questionWords.some(word =>
        correctedText.toLowerCase().startsWith(word) ||
        correctedText.toLowerCase().includes(` ${word}`)
    );
    const lastChar = correctedText[correctedText.length - 1];
    const hasPunctuation = ['.', '?', '!', ',', ';', ':'].includes(lastChar);

    if (!hasPunctuation) {
        if (isQuestion) {
            correctedText += '?';
        } else {
            correctedText += '.';
        }
    }
    correctedText = correctedText.charAt(0).toUpperCase() + correctedText.slice(1);
    return correctedText;
};

const useFontSize = (initialSize = 16) => {
    const [fontSize, setFontSize] = useState(() => {
        const saved = localStorage.getItem('spacy-chat-font-size');
        return saved ? parseInt(saved) : initialSize;
    });
    const increaseFontSize = () => {
        setFontSize(prev => {
            const newSize = Math.min(prev + 2, 24);
            localStorage.setItem('spacy-chat-font-size', newSize.toString());
            return newSize;
        });
    };
    const decreaseFontSize = () => {
        setFontSize(prev => {
            const newSize = Math.max(prev - 2, 14);
            localStorage.setItem('spacy-chat-font-size', newSize.toString());
            return newSize;
        });
    };
    const resetFontSize = () => {
        setFontSize(initialSize);
        localStorage.setItem('spacy-chat-font-size', initialSize.toString());
    };
    return { fontSize, increaseFontSize, decreaseFontSize, resetFontSize };
};

const useAutoScroll = (dependencies = []) => {
    const chatContainerRef = useRef(null);
    const isUserScrollingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    useLayoutEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;

        const handleUserScroll = () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            isUserScrollingRef.current = true;
            scrollTimeoutRef.current = setTimeout(() => {
                isUserScrollingRef.current = false;
            }, 1500);
        };

        container.addEventListener('scroll', handleUserScroll);
        return () => {
            container.removeEventListener('scroll', handleUserScroll);
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    const scrollToBottom = (behavior = 'smooth') => {
        if (isUserScrollingRef.current) return;
        const container = chatContainerRef.current;
        if (!container) return;
        container.scrollTo({ top: container.scrollHeight, behavior: behavior });
    };

    const scrollToElement = (element, behavior = 'smooth') => {
        if (isUserScrollingRef.current || !element) return;
        const container = chatContainerRef.current;
        if (!container) return;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTopInContainer = elementRect.top - containerRect.top + container.scrollTop;
        const elementHalfHeight = elementRect.height / 2;
        const containerHalfHeight = containerRect.height / 2;
        const targetScrollTop = elementTopInContainer - containerHalfHeight + elementHalfHeight;
        container.scrollTo({ top: targetScrollTop, behavior: behavior });
    };

    
    

    useEffect(() => {
        if (!isUserScrollingRef.current) {
            scrollToBottom('smooth');
        }
    }, dependencies);

    return { chatContainerRef, scrollToBottom, scrollToElement, isUserScrolling: isUserScrollingRef.current };
};

const fetchTutorResponse = async (lessonId, message, is_initial = false, is_restart = false) => {
    try {
        const payload = { message, is_initial, is_restart };
        const response = await fetch(`/api/tutor/chat/${lessonId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(payload)
        });
        // El estado se guarda ANTES de lanzar: dentro del catch ya no existe, y
        // sin el todo fallo acababa siendo "problema de conexion" — incluido un
        // 422 por mensaje demasiado largo, que no tiene nada que ver con la red
        // y manda al alumno a mirar su wifi.
        if (!response.ok) {
            const fallo = new Error(`Error en la API: ${response.statusText}`);
            fallo.status = response.status;
            throw fallo;
        }
        return await response.json();
    } catch (error) {
        console.error("No se pudo obtener la respuesta del tutor:", error);
        return {
            reply: motivoDelFallo(error?.status ?? 0),
            current_topic: "Error",
        };
    }
};

const startTutorSession = async (lessonId, setupData) => {
    try {
        console.log("🚀 [START SESSION] Config:", setupData);
        const payload = {
            topic: setupData.topic,
            user_level: setupData.user_level,
            language_mode: setupData.language_mode,
            depth_mode: setupData.depth_mode
        };
        const response = await fetch(`/api/tutor/start-session/${lessonId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            // ── EL MOTIVO VIAJA CON EL FALLO (24-09-2026) ────────────────────
            // Rob, probando lecciones: «ya se rompió, me sale: hubo un error de
            // conexión al iniciar la clase». Cualquier respuesta que no fuera
            // 200 —el freno de 10 clases por minuto, la sesión caducada, un
            // problema del servidor— acababa con ESE mensaje, que acusa a la
            // red de algo que casi nunca es la red. Es el mismo fallo que ya
            // arreglamos con los topes de texto: un aviso que señala al
            // culpable equivocado manda al alumno a mirar su wifi.
            const fallo = new Error(`Error al iniciar sesión: ${response.statusText}`);
            fallo.status = response.status;
            try {
                const cuerpo = await response.json();
                fallo.detalle = typeof cuerpo?.detail === 'string'
                    ? cuerpo.detail : cuerpo?.detail?.message;
            } catch { /* un cuerpo vacío no cambia nada */ }
            throw fallo;
        }
        return await response.json();
    } catch (error) {
        console.error("Critical Error starting session:", error);
        throw error;
    }
};

const fetchChatHistory = async (lessonId) => {
    try {
        const response = await fetch(`/api/tutor/chat/${lessonId}/history`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error(`Error en la API de historial: ${response.statusText}`);
        const data = await response.json();
        return data.history.map(msg => {
            const historyData = msg.parts[0];
            let messageText = '';
            if (msg.role === 'model') {
                try { messageText = JSON.parse(historyData).reply; }
                catch (e) { messageText = historyData; }
            } else {
                messageText = historyData;
            }
            return { speaker: msg.role === 'user' ? 'Student' : 'Spacy', text: messageText };
        });
    } catch (error) {
        console.error("No se pudo obtener el historial del chat:", error);
        return [];
    }
};

// fetchHelpResponse ya no se usa directamente, pero se mantiene por compatibilidad
const fetchHelpResponse = async (lessonId, help_type, current_topic, conversation_history) => {
    try {
        const response = await fetch(`/api/tutor/help/${lessonId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ help_type, current_topic, conversation_history })
        });
        if (!response.ok) throw new Error(`Error en la API de ayuda: ${response.statusText}`);
        return await response.json();
    } catch (error) {
        console.error("No se pudo obtener la respuesta de ayuda:", error);
        return { reply: "Lo siento, tuve un problema al generar la ayuda. Por favor, intenta de nuevo." };
    }
};

const fetchFreshCards = async (lessonId) => {
    try {
        const response = await fetch(`/api/tutor/lesson-details/${lessonId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.visual_gallery_cards || [];
    } catch (e) {
        console.error("Error fetching fresh cards:", e);
        return [];
    }
};

const transcribeAudioWithWhisper = async (audioBlob) => {
    if (!audioBlob) return '';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 35000); // 35s máximo

    try {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        const response = await fetch('/api/tutor/transcribe', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: formData,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            console.error(`Transcribe error: ${response.status}`);
            return '';
        }
        const data = await response.json();
        return data.text || '';
    } catch (e) {
        clearTimeout(timeoutId);
        if (e.name === 'AbortError') {
            console.error('Transcription timeout after 35s');
        } else {
            console.error("Error transcribing audio with Whisper", e);
        }
        return '';
    }
};

// ─── Traducción al español, plegada ──────────────────────────────────────────
//
// Portado de `SpeakingExerciseFree` (el tutor libre), donde lleva tiempo
// funcionando. En el curso, el español venía pegado debajo del inglés en el
// mismo bloque: el ojo lo leía sin querer y el alumno acababa traduciendo en
// vez de entender en inglés.
//
// Ojo con lo que este cambio NO toca: el audio. El curso ya cortaba por `---`
// antes de mandar el texto a la voz (ver `fetchAudioBlob`), así que el español
// nunca ha costado un céntimo de TTS. Esto es sólo cómo se muestra.
const CollapsibleSpanishSection = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="se-spanish-collapsible">
            <button
                className="se-spanish-toggle"
                onClick={() => setIsOpen(!isOpen)}
                type="button"
            >
                <Globe size={14} style={{ marginRight: '6px' }} />
                <span>{isOpen ? 'Ocultar traducción' : 'Ver traducción al español'}</span>
                <span className="toggle-icon" style={{ marginLeft: 'auto' }}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && <div className="se-spanish-content fade-in-up">{children}</div>}
        </div>
    );
};

// Si el mensaje trae el separador, el español baja al acordeón. Si no lo trae
// —el spanglish de A1, que mezcla los idiomas dentro de la misma frase— se
// pinta igual que siempre. La decisión la toma el TEXTO, no el nivel: así no
// hay una regla más que mantener sincronizada con el backend.
const renderSpacyBubble = (text) => {
    const sepIdx = (text || '').search(/\n\s*---\s*\n/);
    if (sepIdx === -1) return <SpacyMessageRenderer text={text} />;
    const eng = text.slice(0, sepIdx).trim();
    const esp = text.slice(sepIdx).replace(/^\s*---\s*\n/, '').trim();
    return (
        <React.Fragment>
            <SpacyMessageRenderer text={eng} />
            <CollapsibleSpanishSection>
                <SpacyMessageRenderer text={esp} />
            </CollapsibleSpanishSection>
        </React.Fragment>
    );
};

// ── EL TOPE DE LA CASILLA LIBRE ──────────────────────────────────────────────
// Este numero NO es decorativo: tiene que caber en el tope del servidor, que
// rechaza el tema entero por encima de 200 caracteres (MAX_TEMA en
// tutor_routes.py). El tema que se envia es `Titulo (Enfoque: lo que escriba)`,
// asi que el presupuesto del alumno es 200 menos el titulo menos la envoltura.
//
// Se calcula en vez de fijarse a pelo para que sea IMPOSIBLE que el frontend
// construya algo que el servidor va a rechazar. El titulo mas largo del
// catalogo mide 55, asi que en la practica siempre salen 120 — el calculo es la
// red de seguridad para el dia que alguien cree una leccion con un titulo largo.
const TEMA_MAX_SERVIDOR = 200;
const ENVOLTURA = ' (Enfoque: )'.length;
const ENFOQUE_MAX = 120;

// El contador se enseña solo cuando queda poco. Un contador siempre visible en
// una casilla OPCIONAL de una linea mete prisa donde no la hay; apareciendo al
// final avisa justo cuando sirve de algo.
const AVISAR_DESDE = 0.75;

function SpacySetupWizard({ onConfigComplete, userLevel, defaultTopic, lessonId }) {
    const [step, setStep] = useState(1);
    // Qué retos de ESTE tema lleva ganados, para marcarlos en el paso 2.
    const ganados = retosDe(lessonId);
    const porGanar = siguienteReto(lessonId, null);
    const [wizardData, setWizardData] = useState({
        branch: '', depth_mode: '', language_mode: '', user_level: userLevel || 'A1'
    });
    const [customInput, setCustomInput] = useState('');
    const limiteEnfoque = Math.max(
        40,
        Math.min(ENFOQUE_MAX,
            TEMA_MAX_SERVIDOR - (defaultTopic?.length || 0) - ENVOLTURA)
    );
    const usados = customInput.length;
    const cercaDelTope = usados >= limiteEnfoque * AVISAR_DESDE;

    const strategies = [
        { id: 'fundamentals', label: 'Fundamentos', icon: <BookOpen size={24} />, desc: 'Reglas claras y corrección detallada.' },
        { id: 'practice', label: 'Práctica Rápida', icon: <Zap size={24} />, desc: 'Minijuegos y diagnóstico veloz.' },
        { id: 'fluency', label: 'Fluidez Total', icon: <Drama size={24} />, desc: 'Conversación inmersiva sin pausas.' }
    ];
    // De A2 en adelante la clase ya no se explica EN español: se da en inglés y
    // el español baja a una traducción de apoyo, en texto y plegada. En A1 sí
    // se mezcla dentro de la frase — el spanglish — porque a ese nivel el
    // alumno todavía lo necesita para no perderse.
    const esBasico = String(wizardData.user_level || 'A1').toUpperCase().startsWith('A1');
    const languages = esBasico
        ? [
            { id: 'spanish_explanations', label: 'Español + Inglés', icon: <MessageCircle size={24} />, desc: 'Explicaciones en español, práctica en inglés.' },
            { id: 'full_english', label: 'Full Immersion', icon: <Globe size={24} />, desc: '100% Inglés. Para valientes.' }
        ]
        : [
            { id: 'spanish_explanations', label: 'Inglés + traducción', icon: <MessageCircle size={24} />, desc: 'La clase en inglés. La traducción al español, en texto, cuando la pidas.' },
            { id: 'full_english', label: 'Full Immersion', icon: <Globe size={24} />, desc: '100% Inglés. Sin red.' }
        ];

    const handleNext = () => {
        if (step === 1) { if (!wizardData.branch) return; setStep(2); }
        else if (step === 2) { if (!wizardData.depth_mode) return; setStep(3); }
    };
    const handleBack = () => { if (step > 1) setStep(step - 1); };

    const handleFinish = () => {
        if (!wizardData.language_mode) return;
        let finalTopicString = defaultTopic || "General Conversation";
        if (customInput.trim().length > 0) {
            finalTopicString = `${defaultTopic} (Enfoque: ${customInput.trim().slice(0, limiteEnfoque)})`;
        }
        // Tercer cinturón. El maxLength del input ya frena al alumno y el
        // servidor rechaza por encima de 200, pero entre los dos queda un hueco:
        // el título de la lección lo pone el catálogo, no el alumno, y si alguien
        // crea una lección con un título larguísimo el tema se pasaría de 200 y
        // la clase moriría con un 422 que el alumno no ha provocado ni entiende.
        // Recortar aquí convierte un error en un tema un poco más corto.
        finalTopicString = finalTopicString.slice(0, TEMA_MAX_SERVIDOR);
        const finalJSON = {
            topic: finalTopicString,
            user_level: wizardData.user_level,
            language_mode: wizardData.language_mode,
            depth_mode: wizardData.depth_mode,
            display_info: { branch: wizardData.branch, formatted_topic: finalTopicString }
        };
        onConfigComplete(finalJSON);
    };

    return (
        <div className="se-integrated-selection-card wizard-container fade-in">
            <div className="se-selection-content wizard-content">
                <div className="se-selection-header wizard-header">
                    <h3 className="se-selection-title">{defaultTopic ? `Lección: ${defaultTopic}` : 'Diseña tu sesión'}</h3>
                    <div className="wizard-progress-bar">
                        <div className={`wizard-step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
                        <div className="wizard-line"></div>
                        <div className={`wizard-step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
                        <div className="wizard-line"></div>
                        <div className={`wizard-step-dot ${step >= 3 ? 'active' : ''}`}>3</div>
                    </div>
                    <p className="se-selection-subtitle">
                        {step === 1 && "Define el contexto"}
                        {step === 2 && "¿Cómo quieres practicar?"}
                        {step === 3 && "¿En qué idioma?"}
                    </p>
                </div>
                {step === 1 && (
                    <div className="wizard-step-body fade-in-right">
                        <div className="se-selection-options">
                            <div className={`se-selection-option ${wizardData.branch === 'academic' ? 'selected' : ''}`} onClick={() => setWizardData(prev => ({ ...prev, branch: 'academic' }))}>
                                <div className="se-selection-radio">{wizardData.branch === 'academic' && <div className="se-selection-radio-dot"></div>}</div>
                                <div className="se-selection-icon"><GraduationCap size={24} /></div>
                                <div className="se-selection-text-content">
                                    <h4 className="se-selection-option-title">Estándar Académico</h4>
                                    <p className="se-selection-option-description">Seguir el plan de estudios oficial.</p>
                                </div>
                            </div>
                            <div className={`se-selection-option ${wizardData.branch === 'business' ? 'selected' : ''}`} onClick={() => setWizardData(prev => ({ ...prev, branch: 'business' }))}>
                                <div className="se-selection-radio">{wizardData.branch === 'business' && <div className="se-selection-radio-dot"></div>}</div>
                                <div className="se-selection-icon"><Briefcase size={24} /></div>
                                <div className="se-selection-text-content">
                                    <h4 className="se-selection-option-title">Escenario Específico</h4>
                                    <p className="se-selection-option-description">Aplicar esta lección a mi trabajo o vida.</p>
                                </div>
                            </div>
                        </div>
                        <div className={`se-business-preference ${wizardData.branch === 'business' ? 'fade-in' : 'hidden'}`} style={{ marginTop: '1rem' }}>
                            <label className="se-preference-label">¿Cuál es tu rol o situación? (Opcional)</label>
                            <input
                                type="text"
                                className="se-preference-input"
                                placeholder="Ej: Mesero en Cancún, Entrevista de React..."
                                value={customInput}
                                maxLength={limiteEnfoque}
                                aria-describedby="se-contador-enfoque"
                                onChange={(e) => setCustomInput(e.target.value.slice(0, limiteEnfoque))}
                            />
                            {/* `aria-live` para que quien navegue con lector de
                                pantalla se entere de que le queda poco: el
                                maxLength del navegador deja de aceptar teclas
                                en silencio, y eso a ciegas parece un teclado
                                roto. */}
                            <div className="se-contador-fila">
                                <span
                                    id="se-contador-enfoque"
                                    className={`se-contador ${cercaDelTope ? 'se-contador-cerca' : ''}`}
                                    aria-live="polite"
                                >
                                    {cercaDelTope
                                        ? `${limiteEnfoque - usados} caracteres restantes`
                                        : `${usados}/${limiteEnfoque}`}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div className="wizard-step-body fade-in-right">
                        <div className="wizard-cards-grid">
                            {strategies.map((strat) => {
                                // Rob, 23-09-2026: «poner reto superado y marcarlo en su
                                // recuadro». Los tres formatos son el MISMO tema exigido de
                                // tres formas, así que enseñar cuáles lleva ganados convierte
                                // tres opciones sueltas en un camino.
                                const ganado = ganados.indexOf(strat.id) !== -1;
                                const esSiguiente = !!porGanar && porGanar.id === strat.id;
                                return (
                                    <div key={strat.id}
                                        className={`wizard-card ${wizardData.depth_mode === strat.id ? 'selected' : ''} ${ganado ? 'superado' : ''} ${esSiguiente ? 'siguiente' : ''}`}
                                        onClick={() => setWizardData(prev => ({ ...prev, depth_mode: strat.id }))}>
                                        <div className="wizard-card-icon">{strat.icon}</div>
                                        <h4>{strat.label}</h4>
                                        <p>{strat.desc}</p>
                                        {ganado && <div className="wizard-chapa">Superado</div>}
                                        {!ganado && esSiguiente && <div className="wizard-chapa wizard-chapa-siguiente">Siguiente</div>}
                                        {wizardData.depth_mode === strat.id && <div className="check-badge"><CheckCircle size={16} /></div>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {step === 3 && (
                    <div className="wizard-step-body fade-in-right">
                        <div className="wizard-cards-grid vertical">
                            {languages.map((lang) => (
                                <div key={lang.id} className={`wizard-card horizontal ${wizardData.language_mode === lang.id ? 'selected' : ''}`} onClick={() => setWizardData(prev => ({ ...prev, language_mode: lang.id }))}>
                                    <div className="wizard-card-icon">{lang.icon}</div>
                                    <div className="wizard-card-text"><h4>{lang.label}</h4><p>{lang.desc}</p></div>
                                    {wizardData.language_mode === lang.id && <div className="check-badge"><CheckCircle size={16} /></div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="wizard-footer">
                    {step > 1 ? (
                        <button className="wizard-btn-secondary" onClick={handleBack}><ArrowLeft size={18} /> Atrás</button>
                    ) : <div></div>}
                    {step < 3 ? (
                        <button className="wizard-btn-primary" onClick={handleNext} disabled={step === 1 ? !wizardData.branch : !wizardData.depth_mode}>
                            Siguiente <ArrowRight size={18} />
                        </button>
                    ) : (
                        <button className="wizard-btn-primary start-btn" onClick={handleFinish} disabled={!wizardData.language_mode}>
                            <Zap size={18} /> Comenzar
                        </button>
                    )}
                </div>
            </div>
            <div className="se-selection-logo"><div className="se-spacy-logo-text">🅢🅟🅐🅒🅨</div></div>
        </div>
    );
}

const VictoryModal = ({ stats, config, onNextLevel, onRestart, onExit }) => {
    let nextBtnLabel = "Siguiente Nivel", nextBtnIcon = <ArrowRight size={18} />, nextBtnSubtitle = "Continuar";
    if (config?.depth_mode === 'fundamentals') { nextBtnLabel = "¡A Practicar!"; nextBtnSubtitle = "Pon a prueba la teoría en un Roleplay"; nextBtnIcon = <Zap size={18} />; }
    else if (config?.depth_mode === 'practice') { nextBtnLabel = "Modo Fluidez 🔥"; nextBtnSubtitle = "Sin pausas, inmersión total"; nextBtnIcon = <Flame size={18} />; }
    else if (config?.depth_mode === 'fluency') { nextBtnLabel = "Modo Maestro 🏆"; nextBtnSubtitle = "Sin muletas: corrección en seco"; nextBtnIcon = <Award size={18} />; }
    else if (config?.depth_mode === 'master') { nextBtnLabel = "Otra ronda 🏆"; nextBtnSubtitle = "Repetir el Modo Maestro"; nextBtnIcon = <Award size={18} />; }

    return (
        <div className="se-victory-overlay">
            <div className="se-victory-card">
                <div className="se-victory-icon">🎓</div>
                <h2 className="se-victory-title">¡Objetivo Logrado!</h2>
                <p className="se-victory-subtitle">Has completado la lección con éxito.</p>
                <div className="se-victory-stats-row">
                    <div className="se-victory-stat-item"><span className="stat-label">Precisión</span><span className="stat-value">{stats.accuracy}%</span></div>
                    <div className="se-victory-stat-item accent"><span className="stat-label">Palabras</span><span className="stat-value">{stats.words}</span></div>
                    <div className="se-victory-stat-item"><span className="stat-label">Frases</span><span className="stat-value">{stats.phrases}</span></div>
                </div>
                <div className="se-victory-actions">
                    <button className="se-btn-primary-v" onClick={onNextLevel}>
                        {nextBtnIcon}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
                            <span>{nextBtnLabel}</span>
                            <span style={{ fontSize: '0.75rem', fontWeight: '400', opacity: 0.9 }}>{nextBtnSubtitle}</span>
                        </div>
                    </button>
                    <button className="se-btn-secondary-v" onClick={onRestart}><RefreshCw size={18} /> <span>Repetir Lección</span></button>
                    <button className="se-btn-secondary-v" onClick={onExit}><ArrowLeft size={18} /> <span>Volver al Menú</span></button>
                </div>
            </div>
        </div>
    );
};

const ChatKeyPointCard = ({ data }) => {
    let card = {};
    try { card = typeof data === 'string' ? JSON.parse(data) : data; } catch (e) { return null; }
    return (
        <div className="se-chat-keypoint fade-in-up">
            <div className="se-keypoint-icon-wrapper">⚔️</div>
            <div className="se-keypoint-content"><h4>{card.title || "Key Concept"}</h4><p>{card.content}</p></div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  COMPONENTE PRINCIPAL — envuelto en ExerciseTokensGuard
// ─────────────────────────────────────────────────────────────────────────────
function SpeakingExercise({ exerciseId, exerciseData, onCorrectAnswer }) {
    const { title, learning_objectives, visual_gallery_cards: initialCards, user_level } = exerciseData?.data || {};
    const navigate = useNavigate();
    const lessonId = exerciseData?.lesson_id ?? exerciseId;

    const [cards, setCards] = useState(initialCards || []);
    const [conversation, setConversation] = useState([]);
    const [userInput, _setUserInput] = useState('');

    // ── EL TOPE, EN EL UNICO SITIO POR EL QUE PASAN TODOS ────────────────────
    // El `maxLength` de la caja frena al alumno cuando ESCRIBE o PEGA, y con eso
    // basta para el uso normal. Pero no actua cuando el texto se mete por
    // programa, y aqui pasa en tres sitios: la transcripcion de la voz, el
    // dictado en vivo y el texto "mejorado". Por ahi cabia un mensaje de
    // cualquier tamano, que ademas no da error hasta que se pulsa enviar.
    //
    // Se recorta en el propio setter para que ningun camino —ni los que se
    // escriban manana— pueda saltarselo. Un tope repartido por siete llamadas
    // es un tope que alguien se va a dejar.
    const setUserInput = useCallback((v) => {
        _setUserInput((anterior) => {
            const siguiente = typeof v === 'function' ? v(anterior) : v;
            return typeof siguiente === 'string'
                ? siguiente.slice(0, LIMITES.mensajeSpacy)
                : siguiente;
        });
    }, []);

    const [isSpacyThinking, setIsSpacyThinking] = useState(false);

    // --- ESTADO DE SUSCRIPCIÓN ---
    const { status } = useSubscriptionStatus();
    const [spacyState, setSpacyState] = useState('neutral');
    const [currentTopic, setCurrentTopic] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [isSpacyMode, setIsSpacyMode] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [stats, setStats] = useState({ words: 0, phrases: 0, accuracy: 100 });
    const [showHistoryButton, setShowHistoryButton] = useState(false);
    const [isHighlightingEnabled, setIsHighlightingEnabled] = useState(true);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showVictoryModal, setShowVictoryModal] = useState(false);
    const [longWait, setLongWait] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [voiceRecognitionError, setVoiceRecognitionError] = useState('');

    // ── OIDO EN VIVO ──────────────────────────────────────────────────────────
    // `live` streamea a Deepgram y va devolviendo texto mientras el alumno
    // habla. Si no consigue abrir el socket, devuelve el audio grabado y se
    // sube por /api/tutor/transcribe igual que siempre: el camino viejo sigue
    // entero justo debajo, como red de seguridad.
    const live = useLiveTranscription();
    const [voiceMetrics, setVoiceMetrics] = useState(null);

    // ── LA VICTORIA ESPERA A QUE SPACY TERMINE DE HABLAR ──────────────────────
    // El banner se abria en cuanto llegaba la respuesta con LESSON_COMPLETE, o
    // sea MIENTRAS Spacy seguia leyendo en voz alta los logros de la clase. El
    // alumno veia el cartel de "objetivo logrado" con una voz de fondo que aun
    // no habia acabado, y no sabia si la leccion habia terminado o no.
    //
    // Ahora la victoria se marca como pendiente y el banner sale cuando el
    // audio de cierre acaba. `victoriaPendienteRef` es un ref y no un estado
    // porque quien lo lee es el `onended` del audio, que se crea una sola vez y
    // se quedaria con el valor viejo si fuera estado.
    const victoriaPendienteRef = useRef(false);
    const [rawTranscript, setRawTranscript] = useState('');

    const currentAudioRef = useRef(null);
    const [showSelectionCard, setShowSelectionCard] = useState(null);
    const [currentConfig, setCurrentConfig] = useState(null);
    // Espejo síncrono de `currentConfig`. `setCurrentConfig` no actualiza la
    // variable dentro de la MISMA llamada, y el audio del primer turno se pide
    // ahí mismo: sin este ref, la voz salía con `lang_mode: null` y A1 perdía
    // el spanglish de OpenAI en su mensaje de bienvenida.
    const configRef = useRef(null);

    const { fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();
    const { chatContainerRef, scrollToBottom, scrollToElement } = useAutoScroll([conversation.length, isSpacyThinking]);
    const conversationContainerRef = useRef(null);
    const textareaRef = useRef(null);

    // FASE 2 REFS
    const wsAudioRef = useRef(null);
    const audioCtxRef = useRef(null);
    const nextStartTimeRef = useRef(0);
// --- FASE 2: WEBSOCKET AUDIO STREAMING (Latencia 0) ---
    useEffect(() => {
        if (!lessonId) return;
        
        const initAudio = () => {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
            }
        };
        document.addEventListener("click", initAudio, { once: true });

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        // Asumiendo que el backend esta en 8002 localmente, pero en prod puede ser la misma url
        const host = window.location.host;
        wsAudioRef.current = new WebSocket(`${protocol}//${host}/api/ws/spacy/stream/${lessonId}`);
        wsAudioRef.current.binaryType = "arraybuffer";

        wsAudioRef.current.onmessage = (event) => {
            if (typeof event.data === "string") {
                if (event.data.includes("completed")) {
                    setIsSpeaking(false);
                    setSpacyState('neutral');
                    isSpeakingRef.current = false;
                }
                return;
            }
            
            const audioCtx = audioCtxRef.current;
            if (!audioCtx) return;

            // Transicion de Spacy a speaking en el primer frame!
            if (!isSpeakingRef.current) {
                setIsSpeaking(true);
                setSpacyState('speaking');
                isSpeakingRef.current = true;
            }

            const pcm16 = new Int16Array(event.data);
            const float32 = new Float32Array(pcm16.length);
            for (let i = 0; i < pcm16.length; i++) {
                float32[i] = pcm16[i] / 32768.0;
            }
            const audioBuffer = audioCtx.createBuffer(1, float32.length, 24000);
            audioBuffer.getChannelData(0).set(float32);
            
            const source = audioCtx.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioCtx.destination);
            
            const playTime = Math.max(audioCtx.currentTime, nextStartTimeRef.current);
            source.start(playTime);
            nextStartTimeRef.current = playTime + audioBuffer.duration;
        };

        return () => {
            if (wsAudioRef.current) wsAudioRef.current.close();
            if (audioCtxRef.current) audioCtxRef.current.close();
            document.removeEventListener("click", initAudio);
        };
    }, [lessonId]);
    // ------------------------------------------------------
    const isSpeakingRef = useRef(false);
    const stepsContainerRef = useRef(null);
    const mainCardRef = useRef(null);
    const lastSpokenMessageIdRef = useRef(null);
    const initializedIdRef = useRef(null);

    const spacyGifMap = { neutral: spacyNormal, listening: spacyListening, speaking: spacySpeaking, thinking: spacyThinking, error: spacyError };

    // Efecto Paciencia y Auto-Scroll del Escáner
    useEffect(() => {
        let timerWait;
        let timerScroll;

        if (isSpacyThinking) {
            timerScroll = setTimeout(() => {
                if (typeof scrollToBottom === 'function') {
                    scrollToBottom('smooth');
                }
            }, 100);

            timerWait = setTimeout(() => setLongWait(true), 4000);
        } else {
            setLongWait(false);
        }

        return () => {
            clearTimeout(timerWait);
            clearTimeout(timerScroll);
        };
    }, [isSpacyThinking, scrollToBottom]);

    useEffect(() => {
        const initializeSession = async () => {
            if (!lessonId) return;
            fetchFreshCards(lessonId).then(freshCards => { if (freshCards && freshCards.length > 0) setCards(freshCards); });
            let savedConfig = null;
            try {
                const savedConfigJSON = localStorage.getItem(`spacy_config_${String(lessonId)}`);
                if (savedConfigJSON) { savedConfig = JSON.parse(savedConfigJSON); configRef.current = savedConfig; setCurrentConfig(savedConfig); }
            } catch (e) { console.error("Error leyendo localStorage:", e); }
            if (showSelectionCard === false) return;
            const history = await fetchChatHistory(lessonId);
            if (history && history.length > 0) {
                setConversation(history); setShowSelectionCard(false); setShowHistoryButton(false);
                setSpacyState('neutral'); lastSpokenMessageIdRef.current = `msg-${history.length - 1}`;
            } else { setShowSelectionCard(true); }
        };
        initializeSession();
    }, [lessonId]);

    useEffect(() => {
        const gifUrlsToPreload = [spacyNormal, spacyListening, spacySpeaking, spacyThinking, spacyError];
        gifUrlsToPreload.forEach((url) => { const img = new Image(); img.src = url; });
    }, []);

    // 🔗 Scroll inteligente: chat → página cuando llega al límite
    useEffect(() => {
        const chatEl = chatContainerRef.current;
        if (!chatEl) return;

        const handleWheel = (e) => {
            const atTop = chatEl.scrollTop <= 0;
            const atBottom = chatEl.scrollTop + chatEl.clientHeight >= chatEl.scrollHeight - 2;
            const goingUp = e.deltaY < 0;
            const goingDown = e.deltaY > 0;

            // Si el chat puede seguir scrolleando → lo consume él
            if ((goingDown && !atBottom) || (goingUp && !atTop)) {
                e.preventDefault();
                chatEl.scrollTop += e.deltaY;
            }
            // Si está en el límite → deja que burbujee a la página (no hace nada)
        };

        chatEl.addEventListener('wheel', handleWheel, { passive: false });
        return () => chatEl.removeEventListener('wheel', handleWheel);
    }, [conversation.length]); // Re-bind cuando llegan mensajes nuevos

    // 🧹 Limpieza al desmontar
    useEffect(() => {
        return () => {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
        };
    }, []);

    const fetchAudioBlob = async (textToSpeak) => {
        if (!textToSpeak) return null;
        let textForAudio = textToSpeak;
        // Si hay traducción, solo leemos la parte en inglés
        if (textForAudio.includes('---')) {
            textForAudio = textForAudio.split('---')[0].trim();
        }
        try {
            const response = await fetch('/api/tutor/speak', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                // `lang_mode` decide el TIMBRE de la voz, no el permiso: el
                // servidor deriva el nivel de la lección por su cuenta, que es
                // lo que de verdad elige proveedor y precio.
                body: JSON.stringify({
                    text: textForAudio,
                    lesson_id: lessonId,
                    lang_mode: configRef.current?.language_mode || null,
                })
            });
            if (!response.ok) return null;
            return await response.blob();
        } catch (e) {
            console.error("Error fetching audio blob", e);
            return null;
        }
    };

    // Abre el banner de victoria, si estaba pendiente. Se llama desde TODOS los
    // finales posibles del audio —terminado, error, o directamente no haberlo—
    // para que la leccion nunca se quede sin cerrar por un fallo de sonido.
    const cerrarLeccionSiTocaba = () => {
        if (!victoriaPendienteRef.current) return;
        victoriaPendienteRef.current = false;
        // Un respiro corto tras el ultimo sonido: encadenar el confeti con la
        // ultima silaba se siente atropellado.
        // El reto ganado se apunta ANTES de enseñar la tarjeta: es lo que hace
        // que el camino se vea con este peldaño ya en verde y el siguiente
        // marcado. Ver `retosSuperados.js`.
        apuntaReto(lessonId, configRef.current?.depth_mode || currentConfig?.depth_mode);
        setTimeout(() => {
            setShowVictoryModal(true);
            launchConfetti();
            showAchievement('¡Reto superado!', '🎉');
        }, 450);
    };

    const processAndPlayResponse = async (responseText, additionalMessages = []) => {
        const audioBlob = await fetchAudioBlob(responseText);
        setIsSpacyThinking(false);
        const spacyMsg = { speaker: 'Spacy', text: responseText };
        setConversation(prev => [...prev, spacyMsg, ...additionalMessages]);

        if (audioBlob) {
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            currentAudioRef.current = audio;
            setSpacyState('speaking');
            isSpeakingRef.current = true;
            setIsSpeaking(true);
            audio.onended = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('neutral');
                URL.revokeObjectURL(audioUrl);
                cerrarLeccionSiTocaba();
            };
            audio.onerror = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('error');
                // Sin sonido tambien hay que cerrar: si no, el alumno termina la
                // leccion y no pasa nada en pantalla.
                cerrarLeccionSiTocaba();
            };
            try {
                await audio.play();
            } catch (err) {
                // El navegador bloqueo la reproduccion (autoplay). No habra
                // `onended`, asi que hay que cerrar la leccion desde aqui.
                setSpacyState('neutral');
                cerrarLeccionSiTocaba();
            }
        } else {
            // Sin audio: el mensaje de cierre ya esta escrito en pantalla, se le
            // da tiempo al alumno a leerlo antes del banner.
            setSpacyState('neutral');
            cerrarLeccionSiTocaba();
        }
    };

    const startRecording = async () => {
        setVoiceRecognitionError('');
        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
        }

        // Al empezar a hablar de nuevo se retira lo del turno anterior: las
        // metricas de la frase pasada no dicen nada de la que viene.
        setVoiceMetrics(null);
        setRawTranscript('');

        try {
            await live.start();
            setIsRecording(true);
            setSpacyState('listening');
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setVoiceRecognitionError("No se pudo acceder al micrófono.");
        }
    };

    const stopRecording = async () => {
        setIsRecording(false);
        setIsTranscribing(true);
        setSpacyState('thinking');

        let resultado;
        try {
            resultado = await live.stop();
        } catch (e) {
            console.error('Fallo al cerrar el oído en vivo:', e);
            resultado = null;
        }

        // ── CAMINO A: el streaming dio texto ──
        // No se sube el audio: Deepgram ya lo escuchó y ya se pagó. Subirlo
        // ademas seria pagar dos veces el mismo minuto por el mismo resultado.
        if (resultado?.texto) {
            const mejorado = improveCapitalization(normalizePunctuation(correctCommonErrors(resultado.texto)));
            setRawTranscript(mejorado);
            setVoiceMetrics(resultado.metricas || null);
            setUserInput(prev => prev ? `${prev} ${mejorado}` : mejorado);
            setIsTranscribing(false);
            setSpacyState('neutral');
            if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
            return;
        }

        // ── CAMINO B: el de siempre ──
        // El socket no abrio, se cayo, o no entendio nada. Se sube el audio
        // completo al endpoint por lotes, exactamente como antes de todo esto.
        if (resultado?.fallbackBlob) {
            await processAudio(resultado.fallbackBlob);
            return;
        }

        setIsTranscribing(false);
        setSpacyState('neutral');
    };

    const handleToggleListening = () => isRecording ? stopRecording() : startRecording();

    const processAudio = async (audioBlob) => {
        setIsTranscribing(true);
        setSpacyState('thinking');
        setVoiceRecognitionError(null);

        try {
            const text = await transcribeAudioWithWhisper(audioBlob);

            // 🔒 VALIDACIÓN DE ERRORES DEL BACKEND:
            if (text && text.includes("Error_")) {
                setVoiceRecognitionError("No pudimos escuchar bien. Intenta acercarte al micrófono.");
                setSpacyState('error');
                return;
            }

            if (text && text.trim().length > 0) {
                const improvedText = improveCapitalization(normalizePunctuation(correctCommonErrors(text)));
                setUserInput(prev => prev ? `${prev} ${improvedText}` : improvedText);
                setSpacyState('neutral');
                if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
            } else {
                setSpacyState('neutral');
            }
        } catch (error) {
            console.error("Error de red en transcripción:", error);
            setVoiceRecognitionError("Error de conexión. Verifica tu red.");
            setSpacyState('error');
        } finally {
            // 🔒 EL ANTÍDOTO OBLIGATORIO:
            setIsTranscribing(false);
        }
    };

    const initializeConversation = async (isRestart = false, setupPayload = null) => {
        if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
        setIsSpeaking(false); isSpeakingRef.current = false; initializedIdRef.current = lessonId;
        lastSpokenMessageIdRef.current = null; setConversation([]); setCurrentTopic(''); setIsCompleted(false);
        setTimeLeft(0); setIsActive(false); setStats({ words: 0, phrases: 0, accuracy: 100 });
        setShowHistoryButton(false); setIsSpacyThinking(true); setSpacyState('thinking'); setShowVictoryModal(false);
        try {
            let initialResponse;
            if (setupPayload) {
                localStorage.setItem(`spacy_config_${String(lessonId)}`, JSON.stringify(setupPayload));
                configRef.current = setupPayload;
                setCurrentConfig(setupPayload);
                const sessionData = await startTutorSession(lessonId, setupPayload);
                initialResponse = { reply: sessionData.message, current_topic: setupPayload.topic, has_history: false };
            } else {
                initialResponse = await fetchTutorResponse(lessonId, "", true, isRestart);
            }
            setCurrentTopic(initialResponse.current_topic);
            if (initialResponse.has_history) setShowHistoryButton(true);
            await processAndPlayResponse(initialResponse.reply);
        } catch (error) {
            // El mensaje dice QUÉ pasó y qué hacer. Sin `status` sí fue la red.
            const motivo = error?.status
                ? (error.detalle || motivoDelFallo(error.status))
                : 'No pude conectar. Revisa tu internet y vuelve a intentarlo.';
            setConversation([{ speaker: 'Spacy', text: `No pude arrancar la clase. ${motivo}` }]);
            setIsSpacyThinking(false); setSpacyState('neutral');
        }
    };

    const handleWizardComplete = (wizardDataJSON) => { setShowSelectionCard(false); initializeConversation(false, wizardDataJSON); };

    useEffect(() => {
        if (lessonId && exerciseData?.data && initializedIdRef.current !== lessonId && showSelectionCard === false && conversation.length === 0) {
            initializeConversation(false, currentConfig);
        }
    }, [lessonId, exerciseData, showSelectionCard]);

    useEffect(() => {
        if (currentTopic && stepsContainerRef.current) {
            const activeStepElement = stepsContainerRef.current.querySelector('.se-lesson-step.active');
            if (activeStepElement) activeStepElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [currentTopic]);

    const speakManual = async (textToSpeak, _messageId) => {
        if (!textToSpeak) return;

        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
        }

        const audioBlob = await fetchAudioBlob(textToSpeak);
        if (audioBlob) {
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            currentAudioRef.current = audio;
            setSpacyState('speaking');
            isSpeakingRef.current = true;
            setIsSpeaking(true);
            audio.onended = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('neutral');
                URL.revokeObjectURL(audioUrl);
            };
            audio.onerror = () => {
                isSpeakingRef.current = false;
                setIsSpeaking(false);
                setSpacyState('error');
            };
            try {
                await audio.play();
            } catch (err) {
                setSpacyState('neutral');
            }
        } else {
            setSpacyState('error');
        }
    };

    useEffect(() => { if (isSpeaking && spacyState !== 'speaking') setSpacyState('speaking'); }, [isSpeaking]);
    useEffect(() => {
        if (!isSpeaking && spacyState === 'speaking') { const t = setTimeout(() => setSpacyState('neutral'), 300); return () => clearTimeout(t); }
        if (!isSpeaking && spacyState === 'error') { const t = setTimeout(() => setSpacyState('neutral'), 1000); return () => clearTimeout(t); }
    }, [isSpeaking, spacyState]);

    const handleSendMessage = async () => {
        let userText = userInput.trim();
        if (!userText || isSpacyThinking) return;
        userText = autoPunctuate(userText);
        if (!isActive) setIsActive(true);
        if (isSpeakingRef.current) {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current = null;
            }
            isSpeakingRef.current = false;
            setIsSpeaking(false);
            setSpacyState('neutral');
        }
        // El alumno pulso enviar con el micro abierto: se descarta lo que
        // estuviera grabando. Antes se paraba la grabacion y su transcrito
        // aterrizaba en el textarea DESPUES de haberlo vaciado al enviar, asi
        // que aparecia colgando en el siguiente turno sin venir a cuento.
        if (isRecording) { live.cancel(); setIsRecording(false); }
        setRawTranscript('');
        setVoiceMetrics(null);
        setConversation(prev => [...prev, { speaker: 'Student', text: userText }]);
        setStats(prev => ({ ...prev, words: prev.words + userText.split(/\s+/).length, phrases: prev.phrases + 1 }));
        setUserInput('');
        if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = '40px'; }
        setIsSpacyThinking(true); setSpacyState('thinking');
        try {
            const response = await fetchTutorResponse(lessonId, userText);
            if ((response.current_topic === "Lección Completada" || response.current_topic === "LESSON_COMPLETE") && !isCompleted) {
                setIsCompleted(true);
                if (onCorrectAnswer) onCorrectAnswer();
                // El confeti y el banner NO salen todavia: primero Spacy dice su
                // mensaje de cierre. Ver `victoriaPendienteRef`.
                victoriaPendienteRef.current = true;
            }
            let extraMessages = [];
            if (response.unlock_gallery_card_id && cards?.length > 0) {
                const cardFound = cards.find(c => c.card_id === response.unlock_gallery_card_id);
                if (cardFound) extraMessages.push({ speaker: 'System-Card', text: JSON.stringify({ title: cardFound.title || cardFound.trigger_topic, content: cardFound.content }) });
            }
            setCurrentTopic(response.current_topic);
            await processAndPlayResponse(response.reply, extraMessages);
        } catch (error) {
            console.error("Error en la conversación con el tutor:", error);
            setConversation(prev => [...prev, { speaker: 'Spacy', text: "Lo siento, tuve un problema. ¿Podemos intentarlo de nuevo?" }]);
            setSpacyState('error'); setIsSpacyThinking(false);
        }
    };

    const handleLoadHistory = async () => { setShowHistoryButton(false); const history = await fetchChatHistory(lessonId); if (history.length > 0) setConversation(prev => [...history, ...prev.slice(1)]); };

    // 🆕 NUEVA VERSIÓN DE handleHelpButton (PATCH v4.0)
    const handleHelpButton = async (type) => {
        if (isSpacyThinking) return;

        setIsSpacyThinking(true);
        setSpacyState('thinking');

        // ── Construir historial limpio para el backend ────────────────────────────
        // Excluimos System-Card y cualquier mensaje de ayuda previo (Spacy-Help)
        // para que el backend vea solo la conversación real entre alumno y Spacy.
        const historyForAI = conversation
            .filter(msg => msg.speaker !== 'System-Card' && msg.speaker !== 'Spacy-Help')
            .map(msg => ({
                role: msg.speaker === 'Student' ? 'user' : 'model',
                parts: [msg.text]
            }));

        const activeTopic = currentTopic || title || 'General';

        // ── Nivel del alumno — viene del Wizard al iniciar la sesión ─────────────
        // currentConfig.user_level es lo que el alumno seleccionó: 'A1', 'B1', etc.
        // Lo normalizamos a basic / intermediate / advanced para los prompts.
        const rawLevel = currentConfig?.user_level || user_level || 'intermediate';
        const studentLevel = (() => {
            const l = rawLevel.toLowerCase();
            if (['a1', 'a2', 'basic', 'beginner'].includes(l)) return 'basic';
            if (['c1', 'c2', 'advanced', 'expert'].includes(l)) return 'advanced';
            return 'intermediate';
        })();

        // ── Modo de práctica — fundamentals | practice | fluency ─────────────────
        const depthMode = currentConfig?.depth_mode || '';

        console.log(`🆘 [HELP] type=${type} | level=${studentLevel} | depth=${depthMode}`);
        console.log(`   last Spacy msg: "${conversation.filter(m => m.speaker === 'Spacy').at(-1)?.text?.slice(0, 80)}..."`);

        try {
            const response = await fetch(`/api/tutor/help/${lessonId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    help_type: type,
                    current_topic: activeTopic,
                    conversation_history: historyForAI,
                    user_level: studentLevel,  // 🆕 nivel calibrado
                    depth_mode: depthMode,     // 🆕 modo de práctica
                })
            });

            if (!response.ok) throw new Error('Help request failed');
            const data = await response.json();

            setConversation(prev => [...prev, {
                speaker: 'Spacy-Help',
                text: data.reply,
                helpType: type
            }]);

        } catch (error) {
            console.error("Error en ayuda:", error);
            setConversation(prev => [...prev, {
                speaker: 'Spacy-Help',
                text: "Lo siento, no pude generar la ayuda. Intenta de nuevo."
            }]);
        } finally {
            setIsSpacyThinking(false);
            if (spacyState === 'thinking') setSpacyState('neutral');
        }
    };

    const toggleHighlighting = () => setIsHighlightingEnabled(prev => !prev);

    const handleNextLevel = (idDestino) => {
        setShowVictoryModal(false);
        let newConfig = { ...currentConfig };
        let sysMsg = "";
        // El alumno puede elegir el peldaño desde la tarjeta de cierre. Si no
        // elige —el camino viejo—, se sigue subiendo de uno en uno.
        if (typeof idDestino === 'string' && idDestino) {
            newConfig.depth_mode = idDestino;
            const nombres = { fundamentals: 'Fundamentos', practice: 'Práctica Rápida',
                              fluency: 'Fluidez Total', master: 'Modo Maestro' };
            sysMsg = `Seguimos con ${nombres[idDestino] || idDestino}. Mismo tema, misma configuración.`;
            initializeConversation(false, newConfig);
            setTimeout(() => setConversation(p => [{ speaker: 'System-Card', text: JSON.stringify({ title: "Siguiente reto", content: sysMsg }) }, ...p]), 500);
            return;
        }
        // La escalera completa. El ultimo peldano ANUNCIABA "Modo Maestro" y no
        // cambiaba el depth_mode: reenviaba 'fluency' y el alumno repetia la
        // misma sesion creyendo que habia subido. Ahora 'master' existe en el
        // backend (build_master_system_prompt) y esto lo manda de verdad.
        if (currentConfig.depth_mode === 'fundamentals') { newConfig.depth_mode = 'practice'; sysMsg = "¡Subiendo de nivel! 🚀 Ahora practica en un escenario real."; }
        else if (currentConfig.depth_mode === 'practice') { newConfig.depth_mode = 'fluency'; sysMsg = "¡Excelente! Ahora modo inmersión total sin pausas. 🔥"; }
        else if (currentConfig.depth_mode === 'fluency') { newConfig.depth_mode = 'master'; sysMsg = "Modo Maestro activado. Sin muletas: Spacy corrige en seco y no acepta respuestas cortas. 🏆"; }
        else { newConfig.depth_mode = 'master'; sysMsg = "Otra ronda en Modo Maestro. 🏆"; }
        initializeConversation(false, newConfig);
        setTimeout(() => setConversation(p => [{ speaker: 'System-Card', text: JSON.stringify({ title: "Flow Ladder Triggered", content: sysMsg }) }, ...p]), 500);
    };

    const restartExercise = async () => {
        localStorage.removeItem(`spacy_config_${String(lessonId)}`);
        configRef.current = null; setCurrentConfig(null); setShowSelectionCard(true); setShowVictoryModal(false); setIsCompleted(false);
        setConversation([]); initializedIdRef.current = null; lastSpokenMessageIdRef.current = null;
        if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    };

    const handleToggleFullscreen = () => setIsFullscreen(prev => !prev);
    const formatTime = (seconds) => { const m = Math.floor(seconds / 60); const s = seconds % 60; return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`; };
    const toggleSpacyMode = () => setIsSpacyMode(prev => !prev);
    const autoGrow = (element) => { if (element) { element.style.height = 'auto'; element.style.height = (element.value === '') ? '40px' : (element.scrollHeight) + 'px'; } };

    useEffect(() => {
        let interval = null;
        if (isActive && !isCompleted) { interval = setInterval(() => setTimeLeft(prevTime => prevTime + 1), 1000); }
        else if (!isActive && timeLeft !== 0) { clearInterval(interval); }
        return () => clearInterval(interval);
    }, [isActive, isCompleted, timeLeft]);

    const getChatStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });
    const getInputStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });
    const getBubbleStyle = () => ({ fontSize: `${fontSize}px`, lineHeight: `${fontSize * 1.4}px` });


    if (showSelectionCard === null || !title) {
        return (
            <div className="exercise-container se-loading">
                <Loader className="spin-animation" size={40} />
                <p style={{ marginTop: 10, color: '#666', fontSize: 14 }}>Cargando lección...</p>
            </div>
        );
    }

    // ── WRAP PRINCIPAL EN ExerciseTokensGuard ─────────────────────────────────
    return (
        <ExerciseTokensGuard>
            <div className={`exercise-container se-exercise-container ${isSpacyMode ? 'spacy-mode-active' : ''} ${isFullscreen ? 'fullscreen-active' : ''}`}>

                {/* ── EL CARTEL DE VICTORIA YA NO SE PINTA (23-09-2026) ──────────
                    Rob, probando una lección: «terminé mi ejercicio y al
                    finalizar me pedía una respuesta final, pero nunca pude
                    responder porque salió este banner… me bloqueó todo el
                    sistema y mientras lo veía, Spacy seguía hablando».

                    El cierre vive ahora DENTRO del chat, como un mensaje más
                    (`<CierreDeLeccion>`, más abajo): se queda en el historial,
                    se puede releer y —lo importante— la caja de escribir nunca
                    deja de funcionar. `VictoryModal` se conserva unas líneas
                    más arriba por si hubiera que volver atrás. */}

                <header className="se-header">
                    <div className="se-icon-container-inset"><MessageCircle size={20} /></div>
                    <div className="se-title-group">
                        <h2 className="main-title">{title || 'Spacy - AI English Tutor'}</h2>
                        <p className="main-subtitle">Practica tu inglés con una IA</p>
                        {!showSelectionCard && currentConfig && (
                            <div className="se-format-badge-wrapper fade-in">
                                <div className="se-format-badge">{currentConfig.display_info?.branch === 'business' ? '💼 Personalizado' : '🎓 Académico'}</div>
                                <button className="se-format-change-btn" onClick={restartExercise} aria-label="Cambiar formato"><RefreshCw size={14} /><span>Reset</span></button>
                            </div>
                        )}
                    </div>
                    <div className="se-controls">
                        <div className="se-font-controls-header">
                            <button className="se-control-btn" onClick={decreaseFontSize} disabled={fontSize <= 14}><ZoomOut size={16} /></button>
                            <div className="se-font-size-display">{fontSize}px</div>
                            <button className="se-control-btn" onClick={increaseFontSize} disabled={fontSize >= 24}><ZoomIn size={16} /></button>
                            {(fontSize !== 16) && <button className="se-control-btn reset" onClick={resetFontSize}><RefreshCw size={14} /></button>}
                        </div>
                        <div className="se-main-controls">
                            <div className="se-stat-box-inset"><Clock size={18} /> {formatTime(timeLeft)}</div>
                            <button className={`se-control-btn ${!isHighlightingEnabled ? 'active' : ''}`} onClick={toggleHighlighting}><Eye size={18} /></button>
                            <button className="se-control-btn" onClick={restartExercise}><RefreshCw size={18} /></button>
                        </div>
                    </div>
                </header>

                {!showSelectionCard && (
                    <>
                        <div className="se-lesson-steps" ref={stepsContainerRef}>
                            {learning_objectives && learning_objectives.map((objective, index) => {
                                const currentTopicIndex = learning_objectives.indexOf(currentTopic);
                                const isComp = currentTopicIndex > index;
                                const isAct = objective === currentTopic;
                                return <div key={index} className={`se-lesson-step ${isAct ? 'active' : ''} ${isComp ? 'completed' : ''}`}><div className="se-step-dot"></div><span className="se-step-text">{objective}</span></div>;
                            })}
                        </div>
                        <div className="se-stats-bar">
                            <div className="se-stat-box-inset large"><div className="se-stat-number">{stats.words}</div><div className="se-stat-label">Palabras</div></div>
                            <div className="se-stat-box-inset large"><div className="se-stat-number">{stats.phrases}</div><div className="se-stat-label">Frases</div></div>
                            <div className="se-stat-box-inset large"><div className="se-stat-number">{stats.accuracy}%</div><div className="se-stat-label">Precisión</div></div>
                        </div>
                    </>
                )}

                <main className="se-main-card" ref={mainCardRef} style={{ position: 'relative' }}>
                    {status === 'tokens' && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1000,
                            background: 'var(--bg-primary)',
                            borderRadius: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(5px)',
                        }}>
                            <PaywallOverlay blockStatus="tokens" isEmbedded={true} />
                        </div>
                    )}

                    {!showSelectionCard && (
                        <div className="se-avatar-wrapper">
                            <div className={`se-avatar ${spacyState}`}>
                                <video
                                    key={spacyGifMap[spacyState]}
                                    src={spacyGifMap[spacyState]}
                                    className="se-spacy-gif"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    aria-label={`Spacy is ${spacyState}`}
                                />
                            </div>
                        </div>
                    )}

                    {showSelectionCard && <SpacySetupWizard onConfigComplete={handleWizardComplete} userLevel={user_level} defaultTopic={title} lessonId={lessonId} />}

                    {!showSelectionCard && (
                        <>
                            <div className="se-top-controls-compact">
                                <div className="se-compact-controls-group">
                                    <button className={`se-spacy-mode-toggle-compact ${isSpacyMode ? 'active' : ''}`} onClick={toggleSpacyMode} aria-label="Alternar Modo Spacy"><Bot size={20} /></button>
                                </div>
                            </div>

                            {/* 🔥 INYECCIÓN: Envolvemos el contenedor con ScanBorderWrapper */}
                            <ScanBorderWrapper isActive={isSpacyThinking}>
                                <div className="se-conversation-history-container" ref={conversationContainerRef}>
                                    <div className="se-chat-messages" ref={chatContainerRef} style={getChatStyle()}>
                                        {showHistoryButton && <div className="se-history-loader"><button onClick={handleLoadHistory}><History size={16} /> Ver historial anterior</button></div>}
                                        {conversation.map((msg, index) => {
                                            if (msg.speaker === 'System-Card') return <div key={index} className="se-chat-system-message"><ChatKeyPointCard data={msg.text} /></div>;
                                            return (
                                                <div key={index} id={`chat-message-${index}`} className={`se-chat-bubble-wrapper ${msg.speaker.toLowerCase()} ${msg.speaker === 'Spacy-Help' ? 'help' : ''}`}>
                                                    <div className="se-avatar-icon">{msg.speaker === 'Spacy' && <Bot size={20} />}{msg.speaker === 'Student' && <User size={20} />}{msg.speaker === 'Spacy-Help' && <AlertCircle size={20} />}</div>
                                                    <div className="se-chat-bubble" style={getBubbleStyle()}>
                                                        {msg.speaker === 'Spacy' || msg.speaker === 'Spacy-Help'
                                                            ? renderSpacyBubble(msg.text)
                                                            : <ReactMarkdown components={{ strong: ({ node, ...props }) => <strong className="se-highlight-text" {...props} />, p: ({ node, ...props }) => <p className="se-chat-paragraph-student" {...props} /> }}>{msg.text}</ReactMarkdown>}
                                                        {msg.speaker === 'Spacy' && <button className="se-speak-bubble-btn" onClick={() => speakManual(msg.text, index)} aria-label="Escuchar de nuevo"><Volume2 size={16} /></button>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {/* 🔥 INYECCIÓN: La nueva burbuja animada */}
                                        {isSpacyThinking && (
                                            <SpacyThinkingBubble
                                                isActive={isSpacyThinking}
                                                mode="tutor"
                                                avatarIcon={<Bot size={20} />}
                                            />
                                        )}

                                        {/* El cierre, como último mensaje de la clase. */}
                                        {showVictoryModal && (
                                            <CierreDeLeccion
                                                lessonId={lessonId}
                                                tema={title}
                                                stats={stats}
                                                config={currentConfig}
                                                onSiguiente={handleNextLevel}
                                                onRepetir={restartExercise}
                                                onSalir={() => navigate('/home')}
                                            />
                                        )}
                                    </div>
                                </div>
                            </ScanBorderWrapper>

                            {/* 🆕 REEMPLAZO: Botones de ayuda con SpacyPanicButtons */}
                            <SpacyPanicButtons
                                mode="tutor"
                                disabled={isSpacyThinking}
                                onHelp={handleHelpButton}
                            />

                            <div className="se-input-area">
                                <div className="se-chat-input-wrapper">
                                    <textarea
                                        ref={textareaRef}
                                        className="se-chat-input"
                                        placeholder={isRecording ? (live.isLive ? "Te escucho..." : "Grabando... habla ahora...") : (isTranscribing ? "Transcribiendo..." : "Escribe tu mensaje...")}
                                        /* Mientras habla ve el texto salir; al soltar vuelve a mandar
                                           `userInput`, que es lo que puede editar y lo que se envia. */
                                        value={isRecording && live.liveText ? `${userInput ? userInput + ' ' : ''}${live.liveText}` : userInput}
                                        onChange={(e) => { setUserInput(e.target.value); autoGrow(e.target); }}
                                        disabled={isSpacyThinking || isRecording || isTranscribing}
                                        maxLength={LIMITES.mensajeSpacy}
                                        style={getInputStyle()}
                                    />

                                    <div className="se-input-icon-group">
                                        <button className={`se-input-icon-btn ${isRecording ? 'listening' : ''}`} onClick={handleToggleListening} disabled={isSpacyThinking || isTranscribing} aria-label={isRecording ? "Detener grabación" : "Iniciar grabación"}>
                                            {isTranscribing ? <Loader className="spin-animation" size={18} /> : <Mic size={18} />}
                                            {isRecording && <div className="se-recording-dot"></div>}
                                        </button>
                                        {userInput && <button className="se-input-icon-btn clear" onClick={() => { setUserInput(''); if (textareaRef.current) textareaRef.current.style.height = '40px'; }} aria-label="Limpiar texto">×</button>}
                                        <button className="se-input-icon-btn send" onClick={handleSendMessage} disabled={!userInput.trim() || isSpacyThinking || isRecording} aria-label="Enviar mensaje"><Send size={18} /></button>
                                    </div>
                                </div>
                                {/* ── EL CONTADOR ──────────────────────────────────
                                    Va DEBAJO de la caja y no dentro. Dentro quedaba
                                    encima del microfono y del boton de enviar: se
                                    leia mal y ademas ensuciaba una zona que ya tiene
                                    tres controles. Aqui no compite con nada.

                                    Y se enseña siempre. La primera version aparecia
                                    al 80%, con el argumento de que "12 de 500" es
                                    ruido; el argumento era mio, no del alumno. Con
                                    la caja vacia el limite no se ve por ningun lado,
                                    y descubrirlo consiste en chocarse con el. */}
                                <div className="se-contador-tope" style={{
                                    textAlign: 'right', padding: '4px 18px 0 0',
                                    fontSize: 11, fontWeight: 700, letterSpacing: '.02em',
                                    pointerEvents: 'none', transition: 'color .2s',
                                    color: userInput.length >= LIMITES.mensajeSpacy
                                        ? '#ef4444'
                                        : avisaDelTope(userInput.length, LIMITES.mensajeSpacy)
                                            ? '#f59e0b'
                                            : 'rgba(148,163,184,0.7)',
                                }}>
                                    {userInput.length}/{LIMITES.mensajeSpacy}
                                </div>
                                {isRecording && (
                                    <div className="se-recording-status-area">
                                        <div className="se-recording-indicator">
                                            <div className="se-pulse-animation"></div>
                                            {/* El estado del oído en vivo se dice aquí, en una palabra.
                                                Sin esto, que el streaming falle es indistinguible de que
                                                funcione: en los dos casos la caja de texto está vacía
                                                mientras el alumno habla. */}
                                            <span>
                                                Grabando audio... (Spanglish soportado)
                                                {live.isLive
                                                    ? ' · texto en vivo'
                                                    : (live.motivoSinVivo ? ` · ${live.motivoSinVivo}` : ' · conectando...')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {isTranscribing && <div className="se-recording-status-area"><div className="se-recording-indicator"><Loader className="spin-animation" size={14} /><span>Procesando tu voz con IA...</span></div></div>}
                                {voiceRecognitionError && <div className="se-voice-error"><AlertCircle size={14} /><span>{voiceRecognitionError}</span></div>}

                                {/* El entrenador de voz. Solo sale despues de hablar, y desaparece
                                    en cuanto se envia el mensaje: no acompana a la conversacion. */}
                                {!isRecording && !isTranscribing && (
                                    <VoiceCoachStrip
                                        metrics={voiceMetrics}
                                        transcript={rawTranscript}
                                        level={configRef.current?.user_level || user_level}
                                        onUseText={(texto) => {
                                            setUserInput(texto);
                                            if (textareaRef.current) setTimeout(() => autoGrow(textareaRef.current), 0);
                                        }}
                                    />
                                )}
                            </div>
                            <div className="se-fullscreen-button-wrapper">
                                <button className={`se-fullscreen-btn ${isFullscreen ? 'active' : ''}`}
                                    onClick={handleToggleFullscreen}
                                    title={isFullscreen ? 'Salir del modo foco' : 'Modo foco: la clase a pantalla completa'}>
                                    {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                                    <span>{isFullscreen ? 'Salir' : 'Foco'}</span>
                                </button>
                            </div>
                        </>
                    )}
                </main>
            </div>
        </ExerciseTokensGuard>
    );
}

export default SpeakingExercise;